/**
 * fp-optimistic-add.js v2 — instant cart-drawer open on the main PDP add-to-cart.
 *
 * THE PROBLEM: tapping ADD TO CART showed nothing for ~2.6s on mobile, because
 * the theme gates the drawer open on the server round-trip (GoKwik cart add
 * ~1s + the ~228KB header-section re-render payload + network), then opens the
 * drawer only once CartAddEvent fires.
 *
 * v2 UX (founder-directed 2026-07-10): on tap the drawer opens IMMEDIATELY and
 * a GHOST ITEM ROW (variant thumbnail + product title + shimmer lines — no
 * message text) appears where the new cart line will land:
 *   - cart already has items → ghost row is inserted inline above the items
 *     list, existing items stay visible.
 *   - cart is empty → a white overlay with the ghost row covers the "your cart
 *     is empty" template until the real cart morphs in.
 * The normal product-form.js flow runs untouched underneath; when its
 * cart:update lands we fade the ghost away revealing the real row. On failure
 * (cart:error / a safety timeout) the drawer STAYS OPEN and a slim inline
 * error line appears — we never close the drawer on the shopper.
 *
 * EMPTY→FILLED FIRST-ADD FIX (the "drawer opens then closes" bug): our early
 * open makes Horizon's own auto-open a no-op, and Horizon's empty→filled swap
 * (startViewTransition 'empty-cart-drawer' in component-cart-items.js) leaves
 * the <dialog> without [open]. `pendingReopen` + `ensureOpen()` re-assert the
 * open after the swap settles; a deliberate dismissal (close/cancel events)
 * clears the flag so we never fight the shopper.
 *
 * Additive + isolated: does NOT modify product-form.js or cart-drawer.js.
 * Scoped to the in-page PDP form only — quick-add modals and the cut-pieces
 * sheet (silent add by design) are excluded.
 *
 * Debug: window.__fpOptAddDebug = true.
 */
(function () {
  'use strict';
  if (window.__fpOptAdd) return;
  window.__fpOptAdd = true;

  var DEBUG = function () { return window.__fpOptAddDebug === true; };
  var log = function () { if (DEBUG()) { try { console.log.apply(console, ['[fp-opt-add]'].concat([].slice.call(arguments))); } catch (e) {} } };

  // Failure backstop ONLY. A real add resolves via cart:update well under this,
  // and server errors surface instantly via cart:error — this timer only fires
  // on a silent network drop. Generous so a slow-but-successful 3G add is never
  // falsely flagged as an error.
  var SAFETY_MS = 7000;
  var active = false;
  var ghost = null;    // inline ghost row (cart already has items)
  var overlay = null;  // full overlay w/ ghost row (cart was empty)
  var realInlineGhost = false; // the inline ghost shows the REAL row (P2) → remove it synchronously on morph, not via fade, so no duplicate row is painted
  var safetyTimer = null;
  var drawerEl = null;
  var dialogEl = null;
  // Set when we optimistically open the drawer; consumed at the resolving
  // cart:update to re-assert the drawer open if the empty→filled swap dropped
  // its [open] state. Deliberately separate from `active`.
  var pendingReopen = false;
  // Set when the shopper deliberately closes the drawer while the add is still
  // in flight: cart-drawer.js #handleCartAdd would force it back open when the
  // CartAddEvent lands. We strip [auto-open] for that window and restore it on
  // the next cart event (the drawer's own listener runs first — script order —
  // so it sees the stripped attribute) or via a backstop timer.
  var autoOpenStripped = false;
  var autoOpenRestoreTimer = null;
  // Set when we removed the dialog's `cart-drawer--empty` class during an
  // empty-cart optimistic open. That class centers the whole drawer as a
  // compact empty-state box — opening WITH it made the first add flash a
  // centered white panel that then snapped into the real side-drawer layout
  // when the item landed. We know an item is coming, so we open in the FINAL
  // (filled) layout immediately and restore the class only if the add fails.
  var emptyClassStripped = false;

  function stripAutoOpen() {
    try {
      if (drawerEl && drawerEl.hasAttribute('auto-open')) {
        drawerEl.removeAttribute('auto-open');
        autoOpenStripped = true;
        if (autoOpenRestoreTimer) clearTimeout(autoOpenRestoreTimer);
        autoOpenRestoreTimer = setTimeout(restoreAutoOpen, SAFETY_MS);
        log('auto-open suppressed (shopper closed drawer mid-add)');
      }
    } catch (e) { log('stripAutoOpen failed', e); }
  }

  function restoreAutoOpen() {
    if (!autoOpenStripped) return;
    autoOpenStripped = false;
    if (autoOpenRestoreTimer) { clearTimeout(autoOpenRestoreTimer); autoOpenRestoreTimer = null; }
    try { if (drawerEl) drawerEl.setAttribute('auto-open', ''); } catch (e) {}
    log('auto-open restored');
  }

  function ensureStyles() {
    if (document.getElementById('fp-opt-add-styles')) return;
    var s = document.createElement('style');
    s.id = 'fp-opt-add-styles';
    s.textContent =
      // Ghost row — mirrors the drawer's concise row metrics (68×85 thumb, 12px gap)
      // so the morph swap to the real row is visually seamless. NOTE the ghost
      // is a CUSTOM TAG (<fp-oadd-ghost>), not a <div>: the section morph
      // matches keyless same-tag elements positionally, and a ghost <div> got
      // converted INTO the [data-fp-xsell] anchor <div> during the post-add
      // morph (destroying the cross-sell row). A foreign tag never matches, so
      // the morph simply discards the ghost as surplus.
      'fp-oadd-ghost{display:grid;grid-template-columns:68px minmax(0,1fr);column-gap:12px;align-items:start;' +
      'padding:0 0 8px;margin:0 0 8px;border-bottom:0.5px solid #EDE8E1;transition:opacity 180ms ease;}' +
      'fp-oadd-ghost.fp-oadd-out{opacity:0;}' +
      '.fp-oadd-thumb{width:68px;height:85px;background:#EDE8E1 50% 0/cover no-repeat;}' + // 68x85, top crop = the drawer's row media (fp-cart-v2.css)
      '.fp-oadd-t{font-size:12.5px;font-weight:400;color:#424245;line-height:1.3;margin:2px 0 10px;' +
      'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-family:var(--font-body--family,"Inter",sans-serif);}' +
      '.fp-oadd-sh{height:10px;background:linear-gradient(90deg,#EDE8E1 25%,#F7F4F0 50%,#EDE8E1 75%);' +
      'background-size:200% 100%;animation:fp-oadd-shimmer 1.1s linear infinite;}' +
      '.fp-oadd-sh--1{width:55%;}.fp-oadd-sh--2{width:35%;margin-top:7px;}' +
      '@keyframes fp-oadd-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}' +
      '@media(prefers-reduced-motion:reduce){.fp-oadd-sh{animation:none;}}' +
      // Empty-cart overlay: a full SKELETON of the final drawer layout (ladder
      // placeholder → ghost item row → pinned footer placeholder), so the
      // first add reads as the real drawer loading — not a separate bare
      // screen that then "becomes" the drawer (founder feedback 2026-07-12).
      // Starts BELOW the drawer header so ✕ stays tappable throughout.
      '.fp-oadd-overlay{position:absolute;top:52px;left:0;right:0;bottom:0;z-index:20;background:#fff;' +
      'display:flex;flex-direction:column;padding:0;opacity:1;transition:opacity 180ms ease;}' +
      '.fp-oadd-overlay.fp-oadd-out{opacity:0;}' +
      '.fp-oadd-overlay fp-oadd-ghost{border-bottom:0;}' +
      '.fp-oadd-skel-ladder{flex:none;padding:14px 20px 11px;border-bottom:0.5px solid #EDE8E1;}' +
      '.fp-oadd-skel-ladder .fp-oadd-sh{margin:0 auto 9px;width:170px;}' +
      '.fp-oadd-skel-track{height:3px;background:#EDE8E1;}' +
      '.fp-oadd-skel-scroll{flex:1 1 auto;min-height:0;padding:14px 20px;}' +
      '.fp-oadd-skel-footer{flex:none;border-top:0.5px solid #EDE8E1;}' +
      '.fp-oadd-skel-frow{display:flex;justify-content:space-between;align-items:center;' +
      'padding:12px 20px;border-bottom:0.5px solid #EDE8E1;}' +
      '.fp-oadd-skel-frow .fp-oadd-sh{margin:0;}' +
      '.fp-oadd-skel-cta{margin:12px 20px 14px;height:46px;' +
      'background:linear-gradient(90deg,#EDE8E1 25%,#F7F4F0 50%,#EDE8E1 75%);' +
      'background-size:200% 100%;animation:fp-oadd-shimmer 1.1s linear infinite;}' +
      // OPTIMISTIC REAL ROW (P2, 2026-07-13): when we can build the row from
      // real PDP data, the ghost shows the actual thumbnail/title/variant/unit
      // price/qty/line-total instead of shimmer bars, so the ~820ms server
      // confirm is invisible. The unit price text is read from the PDP's own
      // price.liquid output — byte-identical to what the cart row renders — so
      // it never "shifts" on the morph (the founder's price-flicker concern).
      // Layout mirrors the concise cart row: thumb | (title · total) / unit /
      // variant / full-width qty stepper.
      // Metrics mirror the REAL concise cart row exactly (cart-products.liquid
      // inline styles: title/unit 11.5px, total 14px/400, variant 11px, stepper
      // 34px buttons + 13px qty + 11.5px label, bin 26px box w/ 14px icon at
      // margin-auto right, controls gap 16px, 68x85 thumb) so the morph swap to
      // the real row moves NOTHING — founder QC 2026-07-13 caught the qty row
      // rendering stretched with no bin and "flickering" into place.
      'fp-oadd-ghost.fp-oadd-real{align-items:start;padding:0 0 12px;margin:0 0 12px;}' +
      '.fp-oadd-real-info{min-width:0;font-family:var(--font-body--family,"Inter",sans-serif);}' +
      '.fp-oadd-real-top{display:flex;justify-content:space-between;align-items:baseline;gap:8px;}' +
      '.fp-oadd-real-title{font-size:11.5px;font-weight:400;color:#424245;line-height:1.3;min-width:0;' +
      'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;margin-bottom:1px;}' +
      '.fp-oadd-real-total{font-size:14px;font-weight:400;color:#424245;letter-spacing:0.2px;white-space:nowrap;flex:none;}' +
      '.fp-oadd-real-unit{font-size:11.5px;font-weight:300;color:#8C8279;margin-top:2px;}' +
      '.fp-oadd-real-unit .u{color:#8C8279;font-weight:300;margin-left:2px;}' +
      '.fp-oadd-real-variant{font-size:11px;font-weight:300;color:#8C8279;margin-top:1px;}' +
      '.fp-oadd-real-qty{display:flex;align-items:center;gap:16px;width:100%;margin-top:6px;}' +
      '.fp-oadd-real-stepper{display:inline-flex;align-items:center;height:34px;' +
      'border:0.5px solid #D4CCC2;color:#424245;}' +
      '.fp-oadd-real-stepper .b{width:34px;height:34px;display:flex;align-items:center;justify-content:center;' +
      'color:#424245;font-size:14px;font-weight:300;line-height:1;}' +
      '.fp-oadd-real-stepper .n{min-width:34px;text-align:center;font-size:13px;font-weight:400;color:#424245;}' +
      '.fp-oadd-real-stepper .l{font-size:11.5px;font-weight:300;color:#8C8279;padding-inline:10px;}' +
      '.fp-oadd-real-bin{width:26px;height:26px;margin-left:auto;display:flex;align-items:center;' +
      'justify-content:center;color:#8C8279;opacity:0.9;flex:none;}' +
      '.fp-oadd-real-bin svg{width:14px;height:14px;}' +
      // Inline error line — drawer stays open, error is announced politely.
      '.fp-oadd-err{position:absolute;top:0;left:0;right:0;z-index:30;background:#EDE8E1;color:#424245;' +
      'font:300 12px/1.4 var(--font-body--family,"Inter",sans-serif);padding:10px 16px;text-align:center;' +
      'opacity:1;transition:opacity 300ms ease;}';
    document.head.appendChild(s);
  }

  // Product title, scoped to the container the ATC lives in. Falls back to
  // og:title rather than a wrong heading.
  function findTitle(btn) {
    try {
      var scope = btn.closest('.product-information') || btn.closest('.shopify-section') || btn.closest('main') || document;
      var el = scope.querySelector('.view-product-title, [class*="product-title"], p[role="heading"][aria-level="3"], h1');
      var t = el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!t) {
        var og = document.querySelector('meta[property="og:title"]');
        var c = og && og.getAttribute('content');
        t = c ? c.replace(/\s*[|–—-]\s*Fabric Pandit.*$/i, '').trim() : '';
      }
      return t.length > 90 ? t.slice(0, 88) + '…' : t;
    } catch (e) { return ''; }
  }

  function buildGhost(imageUrl, title) {
    ensureStyles();
    // Custom tag on purpose — see the CSS note: a <div> ghost gets positionally
    // matched (and mutated) by the section morph; a foreign tag never does.
    var g = document.createElement('fp-oadd-ghost');
    g.className = 'fp-oadd-ghost';
    g.setAttribute('aria-hidden', 'true'); // decorative; theme's live region announces the real add
    g.innerHTML =
      '<div class="fp-oadd-thumb"></div>' +
      '<div class="fp-oadd-info">' +
      (title ? '<div class="fp-oadd-t"></div>' : '') +
      '<div class="fp-oadd-sh fp-oadd-sh--1"></div>' +
      '<div class="fp-oadd-sh fp-oadd-sh--2"></div>' +
      '</div>';
    // Image + title via DOM API — never interpolate into innerHTML.
    if (imageUrl) {
      var thumb = g.querySelector('.fp-oadd-thumb');
      if (thumb) thumb.style.backgroundImage = 'url("' + imageUrl.replace(/"/g, '%22') + '")';
    }
    if (title) {
      var tEl = g.querySelector('.fp-oadd-t');
      if (tEl) tEl.textContent = title;
    }
    return g;
  }

  // Gather everything needed to render the REAL row optimistically. Returns
  // null (→ caller falls back to the shimmer ghost) whenever anything is
  // uncertain, so this can only ever IMPROVE the placeholder, never break it.
  // Bails on: no variant id, variant ALREADY in the drawer (the add would
  // merge — our fresh-line qty/total would be wrong), unreadable/decimal price.
  function gatherRowData(btn, atc, dialogEl) {
    try {
      var scope = btn.closest('.product-information') || btn.closest('.shopify-section') || btn.closest('main') || document;
      var form = btn.closest('form');
      var idInput = form && form.querySelector('input[name="id"]');
      var vid = idInput && idInput.value;
      if (!vid || !/^\d+$/.test(vid)) return null; // variant ids are numeric — also guards the selector below

      // Already in cart? The drawer currently shows the PRE-add cart, and each
      // line's stepper carries data-variant-id. If present, /cart/add.js merges
      // into that line (qty += , total recomputed) — a fresh "qty N" row would
      // mislead, so fall back to the neutral shimmer.
      if (dialogEl && dialogEl.querySelector('cart-quantity-selector-component[data-variant-id="' + vid + '"]')) return null;

      // Unit price from the PDP price element — SAME price.liquid render as the
      // cart row, so the money text matches byte-for-byte and never reflows.
      var priceRoot = scope.querySelector('.price');
      if (!priceRoot) return null;
      // A volume-priced product renders a RANGE ("₹min - ₹max") = two .money
      // spans inside .price, and the first is the lowest break price — NOT the
      // qty-1 unit price. Any >1-money price display is ambiguous → shimmer.
      if (priceRoot.querySelectorAll('.money').length > 1) return null;
      var moneyEl = priceRoot.querySelector('.money');
      var unitMoney = moneyEl && moneyEl.textContent ? moneyEl.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!unitMoney || /\.\d/.test(unitMoney)) return null; // need a whole-rupee price to reproduce the total exactly
      var unitInt = parseInt(unitMoney.replace(/[^\d]/g, ''), 10);
      if (!isFinite(unitInt) || unitInt <= 0) return null;

      var suffixEl = priceRoot.querySelector('.fp-price-unit');
      var suffix = suffixEl && suffixEl.textContent ? suffixEl.textContent.replace(/\s+/g, ' ').trim() : '';

      var qEl = scope.querySelector('quantity-selector-component input[type="number"], .quantity-selector input, input[name="quantity"]');
      var qty = qEl ? parseInt(qEl.value, 10) : 1;
      if (!isFinite(qty) || qty < 1) qty = 1;

      // Line total is computed with toLocaleString('en-IN'), which matches
      // Shopify's INR grouping for every value BELOW ₹1,00,000 but diverges at
      // the lakh boundary (1,19,960 vs 119,960). Above it, fall back to the
      // shimmer rather than risk the total's comma pattern flipping on morph.
      if (unitInt * qty >= 100000) return null;

      // Full selected-variant title: join every checked option so a multi-option
      // product (Colour / Size) matches the cart row, not just option1.
      var checked = scope.querySelectorAll('variant-picker input:checked');
      var variantTitle = checked.length
        ? [].map.call(checked, function (i) { return (i.value || '').replace(/\s+/g, ' ').trim(); }).filter(Boolean).join(' / ')
        : '';

      // Line total: replace only the numeric run in the money string with the
      // formatted product, preserving the exact currency prefix/spacing the
      // shop uses ("₹ 999" → "₹ 1,998"). en-IN grouping matches Shopify's for
      // every value this store reaches (< ₹1,00,000).
      var lineTotalMoney = unitMoney.replace(/[\d,]+/, (unitInt * qty).toLocaleString('en-IN'));

      return {
        imageUrl: atc.getAttribute('data-product-variant-media') || '',
        title: findTitle(btn),
        unitMoney: unitMoney,
        suffix: suffix,
        qty: qty,
        variantTitle: variantTitle,
        unitLabel: /mtr/i.test(suffix) ? 'mts' : 'pcs',
        lineTotalMoney: lineTotalMoney
      };
    } catch (e) { return null; }
  }

  function setText(root, sel, val) {
    var e = root.querySelector(sel);
    if (e && val != null) e.textContent = val;
  }

  function buildRealGhost(d) {
    ensureStyles();
    var g = document.createElement('fp-oadd-ghost'); // same custom tag → morph-immune (see buildGhost note)
    g.className = 'fp-oadd-ghost fp-oadd-real';
    g.setAttribute('aria-hidden', 'true');
    // The real concise row HIDES the variant line for "By meter" (redundant
    // with /mtr + mts labels) — mirror that or the line vanishes on the swap.
    var showVariant = d.variantTitle && d.variantTitle !== 'By meter';
    g.innerHTML =
      '<div class="fp-oadd-thumb"></div>' +
      '<div class="fp-oadd-real-info">' +
      '<div class="fp-oadd-real-top"><span class="fp-oadd-real-title"></span><span class="fp-oadd-real-total"></span></div>' +
      '<div class="fp-oadd-real-unit"><span class="m"></span><span class="u"></span></div>' +
      (showVariant ? '<div class="fp-oadd-real-variant"></div>' : '') +
      // Stepper [− qty label +] + trailing bin — same anatomy as the real
      // quantity-controls (label injected before the + by the theme script).
      '<div class="fp-oadd-real-qty">' +
      '<span class="fp-oadd-real-stepper"><span class="b">−</span><span class="n"></span><span class="l"></span><span class="b">+</span></span>' +
      '<span class="fp-oadd-real-bin">' +
      // exact copy of assets/icon-delete.svg (stroke-width resolved to 1)
      '<svg aria-hidden="true" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>' +
      '<path d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg></span>' +
      '</div>' +
      '</div>';
    if (d.imageUrl) {
      var thumb = g.querySelector('.fp-oadd-thumb');
      if (thumb) thumb.style.backgroundImage = 'url("' + d.imageUrl.replace(/"/g, '%22') + '")';
    }
    setText(g, '.fp-oadd-real-title', d.title);
    setText(g, '.fp-oadd-real-total', d.lineTotalMoney);
    setText(g, '.fp-oadd-real-unit .m', d.unitMoney);
    setText(g, '.fp-oadd-real-unit .u', d.suffix);
    if (showVariant) setText(g, '.fp-oadd-real-variant', d.variantTitle);
    setText(g, '.fp-oadd-real-qty .n', String(d.qty));
    setText(g, '.fp-oadd-real-qty .l', d.unitLabel);
    return g;
  }

  function removeNode(node, fade) {
    if (!node) return;
    if (fade) {
      node.classList.add('fp-oadd-out');
      setTimeout(function () { if (node.parentNode) node.parentNode.removeChild(node); }, 200);
    } else if (node.parentNode) {
      node.parentNode.removeChild(node);
    }
  }

  function cleanup(fade) {
    if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }
    active = false;
    realInlineGhost = false;
    var g = ghost, o = overlay;
    ghost = null; overlay = null;
    removeNode(g, fade);
    removeNode(o, fade);
  }

  // Failure: keep the drawer OPEN (never slam it shut on the shopper), drop the
  // ghost, and show a slim inline error line that fades out.
  // fromTimeout: on the safety timeout we LEAVE pendingReopen armed — a
  // slow-but-successful add can land after 7s, and its empty→filled morph would
  // otherwise close the drawer with the body still scroll-locked (no 'close'
  // event fires on an attribute drop, so the theme never unlocks the page).
  // Shopify's /cart/add 422 text → what the shopper should actually read.
  // The generic "please try again" invited exactly the wrong move on a stock
  // error: a retry that fails the same way (and shows up in GA4 as another
  // fp_atc_error). Aug–Oct 2026: ~5% of add attempts were these 422s, almost
  // all on low-stock By-meter variants. Verified live 2026-10-25 (26 metres
  // against 25 in stock, and 3 sarees against 2):
  //   {"status":422,"description":"Only 25 items were added to your cart due to availability."}
  // Shopify ADDS what is left and still answers 422 — so the drawer has to be
  // refreshed too (refreshCartAfterPartialAdd), or the shopper reads "couldn't
  // add" over a cart that silently grew. Returns { text, partial } or null.
  function errorReason(event) {
    try {
      var d = event && event.detail && event.detail.data;
      var raw = d ? String(d.description || d.message || '').replace(/\s+/g, ' ').trim() : '';
      var t = raw.toLowerCase();
      if (!t) return null;
      // N here is the line's quantity AFTER the add (= the sellable stock), not
      // the delta: 2 in stock, 1 already in the cart, 2 asked for → "Only 2
      // items were added" (verified 2026-10-25 on the test theme).
      var m = t.match(/^only (\d+) items? (?:was|were) added/);
      if (m) return { text: 'Only ' + m[1] + ' in stock — all ' + m[1] + ' are now in your cart', partial: true };
      if (t.indexOf('sold out') !== -1) return { text: 'Sorry — this just sold out', partial: false };
      m = t.match(/^all (\d+) .* are in your cart/);
      if (m) return { text: 'All ' + m[1] + ' in stock are already in your cart', partial: false };
      if (t.indexOf('in your cart') !== -1 || t.indexOf('add more') !== -1 || t.indexOf('not enough') !== -1 || t.indexOf('maximum') !== -1) {
        return { text: 'You already have all the available stock in your cart', partial: false };
      }
      return raw.length <= 110 ? { text: raw, partial: false } : null;
    } catch (e) { return null; }
  }

  // After a PARTIAL add the cart HAS changed but nothing re-renders it:
  // product-form.js dispatches its didError CartAddEvent and cart-optimistic.js
  // stops that in the capture phase (so the drawer never morphs and the bubble
  // never moves). Re-fetch the cart and the drawer section(s) and dispatch a
  // bare cart:update carrying both — the shape fp-cart-xsell.js uses for its
  // silent adds: cart-items-component hydration-morphs the drawer (the open
  // <dialog> is never touched, so it stays modal), cart-icon takes
  // resource.items.length, product-form picks up the new in-cart quantity,
  // and fp-analytics ignores the unlisted source. Two GETs, no cart mutation.
  // text: the notice to keep on screen. The drawer's own re-render (another
  // listener answers the same cart:update with a full renderSection) replaces
  // the <dialog>'s children and takes the notice with it ~1-2 s later, so it
  // is re-appended after the morph settles — never duplicated.
  function refreshCartAfterPartialAdd(text) {
    try {
      var ids = [];
      document.querySelectorAll('cart-items-component').forEach(function (el) {
        var id = el.dataset && el.dataset.sectionId;
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var cartReq = fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.ok ? r.json() : null; });
      var sectionsReq = ids.length
        ? fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : {}; }).catch(function () { return {}; })
        : Promise.resolve({});
      Promise.all([cartReq, sectionsReq])
        .then(function (res) {
          var cart = res[0], sections = res[1] || {};
          if (!cart || !Array.isArray(cart.items)) return;
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, sourceId: 'fp-optimistic-add', data: { source: 'fp-partial-add', itemCount: cart.items.length, sections: sections } }
          }));
          log('partial add — drawer refreshed', { lines: cart.items.length, sections: Object.keys(sections) });
          if (text) {
            [100, 900, 1800, 3000].forEach(function (ms) {
              setTimeout(function () {
                if (!document.querySelector('.fp-oadd-err')) notice(text, 5000);
              }, ms);
            });
          }
        })
        .catch(function (e) { log('partial-add refresh failed', e); });
    } catch (e) { log('refreshCartAfterPartialAdd failed', e); }
  }

  // Slim status line at the top of the OPEN drawer (no-op when it is closed).
  // Resolves the dialog fresh: a morph can replace the element we cached.
  function notice(text, hold) {
    try {
      var d = document.querySelector('cart-drawer-component .cart-drawer__dialog') || document.querySelector('cart-drawer-component dialog');
      if (!d || !d.open) return;
      ensureStyles();
      var e = document.createElement('div');
      e.className = 'fp-oadd-err';
      e.setAttribute('role', 'status');
      e.textContent = text;
      d.appendChild(e);
      setTimeout(function () { e.classList.add('fp-oadd-out'); e.style.opacity = '0'; }, hold);
      setTimeout(function () { removeNode(e, false); }, hold + 500);
    } catch (err) { log('notice failed', err); }
  }

  // reason: a shopper-facing sentence from errorReason(), or null/undefined
  // for the generic line (safety timeout, unknown error).
  function fail(fromTimeout, reason) {
    log('fail — showing inline error, drawer stays open', { fromTimeout: !!fromTimeout, reason: reason || null });
    if (!fromTimeout) pendingReopen = false;
    // The add didn't land: if we pre-stripped the empty-state styling and the
    // cart is genuinely still empty, put it back so the drawer looks right.
    if (emptyClassStripped) {
      emptyClassStripped = false;
      try {
        if (dialogEl && !dialogEl.querySelector('.cart-form')) dialogEl.classList.add('cart-drawer--empty');
      } catch (e0) {}
    }
    cleanup(false);
    // A specific reason needs a moment longer to read than the generic line.
    notice(reason || 'Couldn’t add to cart — please try again', reason ? 6000 : 3800);
  }

  // Re-assert the drawer open if the empty→filled swap left the <dialog>
  // without [open]. No-op on the common non-empty add. Re-queries the dialog
  // each call because the swap can replace the element.
  // Reopen helper: if the body is still scroll-locked from OUR first open,
  // going through the full showDialog() again would read window.scrollY as 0
  // (body is position:fixed) and overwrite the saved scroll position — the page
  // snaps to the top behind the drawer and the shopper is stranded at the top
  // on close. Re-open the bare <dialog> instead; the theme's lock + saved
  // position are still intact.
  function reopen(dc, d) {
    if (document.body.style.position === 'fixed' && typeof d.showModal === 'function') {
      try { d.showModal(); return; } catch (e) { /* fall through */ }
    }
    dc.showDialog();
  }

  // The stuck "strip" state: a morph re-slotted the OPEN modal dialog, which
  // silently drops it from the top layer WITHOUT firing 'close' — it collapses
  // into a non-modal "CART n ✕" bar at the top of the page with [open] stuck
  // true, and because dialog.open reads true, every showDialog() (ours AND the
  // theme's) no-ops forever after. Detect = open && not :modal. Repair =
  // close() (clears [open], fires a clean 'close') so a real open can run.
  function isStuckStrip(d) {
    try { return !!(d && d.open && !d.matches(':modal')); } catch (e) { return false; }
  }

  function ensureOpen() {
    try {
      var dc = drawerEl || document.querySelector('cart-drawer-component[auto-open]');
      if (!dc || typeof dc.showDialog !== 'function') return;
      var d = dc.querySelector('.cart-drawer__dialog') || dc.querySelector('dialog');
      if (!d) return;
      if (isStuckStrip(d)) {
        log('repairing stuck non-modal drawer strip');
        try { d.close(); } catch (e3) {}
        reopen(dc, d);
        return;
      }
      if (!d.open) {
        log('re-assert drawer open (swap closed it)');
        reopen(dc, d);
      }
    } catch (e) { log('ensureOpen failed', e); }
  }

  // --- resolution listeners (installed once) ---
  document.addEventListener('cart:update', function (event) {
    // The drawer's own listener for this event already ran (registration
    // order), so a mid-add close's suppression window can end now.
    restoreAutoOpen();

    // didError branch is defensively redundant — cart-optimistic.js suppresses
    // didError cart:update in the CAPTURE phase, so this BUBBLE listener never
    // actually receives one; rollback runs via 'cart:error' below. Kept correct
    // in case that suppression ever changes.
    var didError = event && event.detail && event.detail.data && event.detail.data.didError === true;
    var src = event && event.detail && event.detail.data && event.detail.data.source;

    if (active) {
      // Attribute resolution to OUR add (the PDP product form). A concurrent
      // foreign cart:update — drawer qty +/- ('cart-items-component'), a
      // cut-piece silent add ('fp-cut-pieces'), a cross-sell quick-add
      // ('fp-xsell') — must NOT fade the ghost early or disarm error handling:
      // the new PDP line isn't in that morph yet.
      var isOurs = src === 'product-form-component' || src === undefined || src === null;
      if (didError) { if (isOurs) { fail(); return; } }
      else if (isOurs) {
        // Success: the cart is filled now — the pre-stripped empty-state class
        // stays off (the morph-sync in component-cart-items keeps it correct).
        emptyClassStripped = false;
        if (realInlineGhost && ghost) {
          // The real cart row was morphed in by cart-items-component's listener,
          // which registered at module-eval and so ran earlier in THIS same
          // cart:update dispatch (its script loads before ours). Our inline
          // ghost is an identical row sitting just above it — remove it
          // SYNCHRONOUSLY, before the browser paints, so no duplicate row
          // flashes. No fade: the real row is already in place, so the swap is
          // invisible rather than a cross-dissolve of two identical rows.
          cleanup(false);
        } else {
          // Shimmer ghost / empty-cart overlay: let the morph settle a frame,
          // then fade the placeholder away to reveal the real row underneath.
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { cleanup(true); });
          });
        }
      }
    }

    // Independent of `active` (a spurious transition-close may already have
    // reset it) and of source (ANY morph can drop [open]). Staggered ticks
    // cover view-transition timing; each is a no-op once stably open.
    if (pendingReopen && !didError) {
      pendingReopen = false;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          ensureOpen();
          setTimeout(ensureOpen, 250);
          setTimeout(ensureOpen, 550);
        });
      });
    }
  }, false);

  document.addEventListener('cart:error', function (event) {
    restoreAutoOpen();
    var reason = errorReason(event);
    if (active) {
      fail(false, reason && reason.text);
    } else if (reason && reason.partial) {
      // Not our optimistic open (drawer already open, or a quick-add modal) —
      // still say what actually happened if the drawer is showing.
      notice(reason.text, 6000);
    }
    // Let product-form.js finish its own error handling for this tick first.
    if (reason && reason.partial) setTimeout(function () { refreshCartAfterPartialAdd(reason.text); }, 0);
  }, false);

  function onClick(event) {
    try {
      if (active) return; // one optimistic open at a time

      var btn = event.target && event.target.closest ? event.target.closest('button[name="add"]') : null;
      if (!btn) return;
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

      var atc = btn.closest('add-to-cart-component');
      if (!atc) return; // only the real product-form ATC

      // Scope to the in-page PDP form: exclude quick-add modals, the cut-pieces
      // sheet, and anything already inside an open dialog.
      if (btn.closest('dialog, .quick-add-modal, quick-add-modal, #fp-cp-dialog, quick-add-component, .card-gallery, product-card')) return;

      // Only when the drawer is the cart UI AND set to auto-open AND closed.
      drawerEl = document.querySelector('cart-drawer-component[auto-open]');
      if (!drawerEl || typeof drawerEl.showDialog !== 'function') return;
      dialogEl = drawerEl.querySelector('.cart-drawer__dialog') || drawerEl.querySelector('dialog');
      if (!dialogEl) return;
      if (dialogEl.open) {
        // Genuinely open (modal) → nothing to do. But if it's the stuck
        // non-modal strip (see isStuckStrip), repair it and continue with the
        // optimistic open — this makes an ATC tap self-heal the strip instead
        // of silently doing nothing.
        if (!isStuckStrip(dialogEl)) return;
        log('ATC tap found stuck strip — repairing before open');
        try { dialogEl.close(); } catch (eS) {}
      }

      // Respect client-side validity: if the form is invalid product-form.js
      // aborts the add — so we must not open either.
      var form = btn.closest('form');
      if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) return;

      // Replicate product-form's max-quantity guard: exceeding max aborts with
      // NO fetch (no cart:update, no cart:error) — bail so we don't strand an
      // open drawer on the safety timeout.
      try {
        var pf = btn.closest('product-form-component');
        var qs = pf && pf.refs ? pf.refs.quantitySelector : null;
        if (qs && typeof qs.canAddToCart === 'function') {
          var validation = qs.canAddToCart();
          if (validation && validation.canAdd === false) return;
        }
      } catch (e) { /* can't determine → fall through; the timeout backstops it */ }

      // Real-row data (P2) — gathered BEFORE showDialog so the already-in-cart
      // check reads the pre-add drawer content. null → shimmer fallback.
      var rowData = gatherRowData(btn, atc, dialogEl);
      var imageUrl = (rowData && rowData.imageUrl) || atc.getAttribute('data-product-variant-media') || '';
      var title = (rowData && rowData.title) || findTitle(btn);

      // Open the drawer NOW (correct scrollY captured here; the later
      // CartAddEvent open no-ops via the drawer's dialog-open guard).
      drawerEl.showDialog();

      // Anchor overlay/error positioning even if the UA left the dialog static.
      try { if (getComputedStyle(dialogEl).position === 'static') dialogEl.style.position = 'relative'; } catch (e) {}

      // Non-empty cart → inline ghost row above the items list (existing items
      // stay visible). Empty cart (no .cart-form rendered) → overlay w/ ghost.
      var cartForm = dialogEl.querySelector('.cart-form');
      emptyClassStripped = false;
      realInlineGhost = false;
      if (cartForm && cartForm.parentNode) {
        ghost = rowData ? buildRealGhost(rowData) : buildGhost(imageUrl, title);
        realInlineGhost = !!rowData;
        cartForm.parentNode.insertBefore(ghost, cartForm);
      } else {
        ensureStyles();
        // Open the empty drawer in its FINAL (filled) layout: the empty-state
        // class centers everything as a compact box, which made the first add
        // flash a centered panel before snapping into the side drawer. An item
        // is coming — drop the class now; fail() restores it if the add dies.
        if (dialogEl.classList.contains('cart-drawer--empty')) {
          dialogEl.classList.remove('cart-drawer--empty');
          emptyClassStripped = true;
        }
        // Full-layout skeleton: ladder placeholder → ghost row → pinned footer
        // placeholder — same architecture as the filled drawer, so the morph
        // reads as content loading INTO the drawer, not a screen swap.
        overlay = document.createElement('div');
        overlay.className = 'fp-oadd-overlay';
        overlay.setAttribute('aria-hidden', 'true');

        var ladder = document.createElement('div');
        ladder.className = 'fp-oadd-skel-ladder';
        ladder.innerHTML = '<div class="fp-oadd-sh"></div><div class="fp-oadd-skel-track"></div>';

        var scroll = document.createElement('div');
        scroll.className = 'fp-oadd-skel-scroll';
        // Empty cart → the add is always a fresh line, so the real row is safe
        // here whenever we could build it. The overlay still fades on morph
        // (it covers the whole drawer, so no duplicate-row concern).
        scroll.appendChild(rowData ? buildRealGhost(rowData) : buildGhost(imageUrl, title));

        var footer = document.createElement('div');
        footer.className = 'fp-oadd-skel-footer';
        footer.innerHTML =
          '<div class="fp-oadd-skel-frow"><div class="fp-oadd-sh" style="width:45%;"></div><div class="fp-oadd-sh" style="width:14%;"></div></div>' +
          '<div class="fp-oadd-skel-frow" style="border-bottom:0;"><div class="fp-oadd-sh" style="width:22%;"></div><div class="fp-oadd-sh" style="width:18%;"></div></div>' +
          '<div class="fp-oadd-skel-cta"></div>';

        overlay.appendChild(ladder);
        overlay.appendChild(scroll);
        overlay.appendChild(footer);

        // Sit the overlay exactly under the live header (fallback 52px).
        try {
          var hdr = dialogEl.querySelector('.fp2-header');
          if (hdr) overlay.style.top = Math.ceil(hdr.getBoundingClientRect().height) + 'px';
        } catch (eH) {}

        dialogEl.appendChild(overlay);
      }
      active = true;
      pendingReopen = true;

      // A real 'close' fires only on deliberate closes (close button →
      // closeDialog(), Esc → cancel+close, backdrop, Android back) — NOT when
      // the morph drops [open]. So on close: stop everything, don't reopen,
      // and suppress the drawer's own auto-open until the in-flight add lands —
      // otherwise cart-drawer.js #handleCartAdd pops the drawer back open ~1-2s
      // after the shopper explicitly dismissed it.
      dialogEl.addEventListener('close', function onClose() {
        dialogEl.removeEventListener('close', onClose);
        pendingReopen = false;
        if (active) {
          stripAutoOpen();
          cleanup(false);
        }
      });
      dialogEl.addEventListener('cancel', function onCancel() {
        dialogEl.removeEventListener('cancel', onCancel);
        pendingReopen = false;
      });

      safetyTimer = setTimeout(function () { fail(true); }, SAFETY_MS);
      log('optimistic open', { mode: cartForm ? 'inline-ghost' : 'empty-overlay', title: title });
    } catch (e) {
      log('onClick error', e);
      // Never break the real add-to-cart: swallow and let the normal flow run.
      cleanup(false);
    }
  }

  // Capture phase so we run before product-form's own handlers. We only READ +
  // open a drawer — never preventDefault — so the real submit proceeds normally.
  document.addEventListener('click', onClick, true);

  log('installed v2');
})();
